import hotelDayPassesByLocation from '@constants/hotelDayPassesBylocation';
import { amenityTitles, productTypeTitles, SrpSeoFAQs } from '@constants/srpSeoTitles';
import { SEOCityAmenities } from '@customTypes/seoCityPages';
import { GetDefaultSearchState } from '@context/SearchContext2025';
import { GenerateSearchUrl } from '@helpers/url';

interface SeoLink {
  text: string;
  href: string;
}

interface SeoLinkWithPriority extends SeoLink {
  sort: number;
}

export interface SeoLinks {
  title: string;
  links: SeoLink[];
}

const categoryToLinks = {
  all: 'Top Locations',
  spa: 'Top Spas',
  pool: 'Top Pools',
  'day room': 'Top Day Rooms',
};

export function getGreatCitiesLinks(category: keyof typeof categoryToLinks): SeoLinks {
  const productCategory = category || 'all';
  const title = `Explore cities with great hotel${
    productCategory === 'all' ? ' day passes' : ` ${productCategory}s`
  }`;

  const section = hotelDayPassesByLocation.find(
    (group) => group.title === categoryToLinks[productCategory],
  );

  const links =
    section?.data.map((item) => ({
      text: productCategory === 'all' ? `Hotel day passes in ${item.name}` : item.name,
      href: item.url,
    })) || [];

  return { title, links };
}

const amenityOrProductTypeSortOrder: Partial<
Record<keyof (typeof amenityTitles & typeof productTypeTitles), number>
> = {
  'infinity-pool': 0,
  'indoor-pool': 1,
  waterpark: 2,
  'rooftop-pool': 3,
  1: 4,
  4: 5,
  'splash-pad': 6,
  hottub: 7,
  'outdoor-pool': 8,
  'kiddie-pool': 9,
  waterslide: 10,
  lazyriver: 11,
  7: 12,
  3: 13,
  sauna: 14,
  'steam-room': 15,
  53: 16,
  51: 17,
  50: 18,
  'cold-plunge': 19,
  'infrared-sauna': 20,
  'salt-room': 21,
  'thermal-pool': 22,
  'led-and-red-light-therapy': 23,
  beach: 24,
  'all-inclusive': 25,
  'luggage-storage': 26,
  'airport-shuttle': 27,
  'cruise-shuttle': 28,
};

const getAmenityLinks = (
  amenityAndProductTypes: Array<SEOCityAmenities[string]>,
  countryCode: string,
  stateCode: string,
  city: string,
  locationName: string,
  category: string,
): SeoLinkWithPriority[] => {
  const amenities = amenityAndProductTypes.filter(
    (item) =>
      item.type === 'amenity' &&
      amenityTitles[item.name] &&
      amenityOrProductTypeSortOrder[item.name],
  );
  const pc = category || 'all';
  const defaultSearchState = GetDefaultSearchState();
  return amenities.flatMap((amenity) => {
    const productCategoryTitles = amenityTitles[amenity.name];
    return (
      Object.keys(productCategoryTitles)
        // Get only titles that are not empty and not in the current category
        .filter(
          (productCategory) =>
            productCategory !== pc && productCategoryTitles[productCategory] !== '',
        )
        // Map to links
        .map((productCategory) => ({
          sort: amenityOrProductTypeSortOrder[amenity.name]!,
          text: `${productCategoryTitles[productCategory]} in ${locationName}`,
          href: GenerateSearchUrl({
            ...defaultSearchState,
            city,
            state_code: stateCode,
            country_code: countryCode,
            product_category: productCategory,
            filters: {
              ...defaultSearchState.filters,
              amenities: {
                ...defaultSearchState.filters.amenities,
                [amenity.name]: true,
              },
            },
          }),
        }))
    );
  });
};

const getProductTypeLinks = (
  amenityAndProductTypes: Array<SEOCityAmenities[string]>,
  countryCode: string,
  stateCode: string,
  city: string,
  locationName: string,
  category: string,
): SeoLinkWithPriority[] => {
  const productTypes = amenityAndProductTypes.filter(
    (item) =>
      item.type === 'product_type' &&
      productTypeTitles[item.id] &&
      amenityOrProductTypeSortOrder[item.id],
  );
  const defaultSearchState = GetDefaultSearchState();
  const pc = category || 'all';
  return productTypes.flatMap((productType) => {
    const productCategoryTitles = productTypeTitles[productType.id];
    return (
      Object.keys(productCategoryTitles)
        // Get only titles that are not empty and not in the current category
        .filter(
          (productCategory) =>
            productCategory !== pc && productCategoryTitles[productCategory] !== '',
        )
        // Map to links
        .map((productCategory) => ({
          sort: amenityOrProductTypeSortOrder[productType.id]!,
          text: `${productCategoryTitles[productCategory]} in ${locationName}`,
          href: GenerateSearchUrl({
            ...defaultSearchState,
            city,
            state_code: stateCode,
            country_code: countryCode,
            product_category: productCategory,
            filters: {
              ...defaultSearchState.filters,
              product_types: [+productType.id],
            },
          }),
        }))
    );
  });
};

export function getExperiencesInCityLinks(
  countryCode: string,
  stateCode: string,
  city: string,
  locationName: string,
  category: string,
  cityAmenities: SEOCityAmenities,
): SeoLinks {
  const amenityAndProductTypes = Object.values(cityAmenities);
  const cityAmenityLinks = getAmenityLinks(
    amenityAndProductTypes,
    countryCode,
    stateCode,
    city,
    locationName,
    category,
  );
  const productTypeLinks = getProductTypeLinks(
    amenityAndProductTypes,
    countryCode,
    stateCode,
    city,
    locationName,
    category,
  );
  const allLinks = [...cityAmenityLinks, ...productTypeLinks];
  allLinks.sort((a, b) => a.sort - b.sort);
  return { title: `Explore more experiences in ${locationName}`, links: allLinks };
}

export function getFAQs(category: string, locationName: string) {
  const replaceLocation = (text: string, location: string) => text.replace(/__CITY__/g, location);
  const productCategory = category || 'all';
  return (SrpSeoFAQs[productCategory] || []).map((question) => ({
    q: replaceLocation(question.q, locationName),
    a: replaceLocation(question.a, locationName),
  }));
}
